import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { AttributionField } from "./shared/AttributionField";
import { ChangeLog } from "./shared/ChangeLog";
import { imprestCategoryId, isImprestCategory, reshapeForCategory } from "./shared/imprestCategory";
import VendorFormDialog from "../vendorMaster/VendorFormDialog";

const VENDOR_MANAGE_ROLES = ["Director", "Manager", "Back Office"];

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

// One Cash or UPI post-facto entry. New entries take their mode from the
// tab. Existing entries are corrected (`correctionMode`) within the 15-day
// window — any field, Cash<->UPI switchable, every change logged; outside
// the window the dialog is read-only with the change log.
export default function PostFactoEntryFormDialog({ mode, entry, refData, readOnly, correctionMode, imprestPreset, onClose, onSaved }) {
  useEffect(() => { refData.refresh(["vendors", "sites", "equipment"]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { user } = useAuth();
  const canManageVendor = !readOnly && VENDOR_MANAGE_ROLES.includes(user?.role);
  const isEdit = !!entry;
  const [saving, setSaving] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [outstandingBills, setOutstandingBills] = useState([]);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [form, setForm] = useState({
    entity_code: entry?.entity_code || "",
    site_id: entry?.site_id || "",
    equipment_id: entry?.equipment_id || "",
    category_id: entry?.category_id || (imprestPreset ? imprestCategoryId(refData.categories) || "" : ""),
    vendor_code: entry?.vendor_code || "",
    vendor_bill_id: entry?.vendor_bill_id || "",
    bill_amount_applied: entry?.bill_amount_applied ?? "",
    amount: entry?.amount ?? "",
    payment_date: entry?.payment_date ? entry.payment_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    reference: entry?.reference || "",
    purpose: entry?.purpose || "",
    mode: entry?.mode || mode,
    payee_type: entry?.payee_type || (imprestPreset ? "Employee" : entry?.vendor_code ? "Vendor" : "Other"),
    payee_employee_id: entry?.payee_employee_id || imprestPreset?.employeeId || "",
    payee_note: entry?.payee_note || "",
  });
  const [payeeTouched, setPayeeTouched] = useState(isEdit);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const isImprest = isImprestCategory(refData.categories, form.category_id);

  useEffect(() => {
    if (!form.vendor_code) {
      setOutstandingBills([]);
      return;
    }
    client
      .get("/payment-desk/bills/for-vendor/outstanding", { params: { vendor_code: form.vendor_code } })
      .then((res) => setOutstandingBills(res.data))
      .catch(() => setOutstandingBills([]));
  }, [form.vendor_code]);

  const selectedBill = outstandingBills.find((b) => b.bill_id === form.vendor_bill_id);

  const changeVendor = (code) =>
    setForm((f) => {
      const next = { ...f, vendor_code: code, vendor_bill_id: "" };
      if (!code && f.payee_type === "Vendor") next.payee_type = "Other";
      else if (code && !payeeTouched) next.payee_type = "Vendor";
      return next;
    });
  const base = mode === "Cash" ? "/payment-desk/cash" : "/payment-desk/upi";
  const switchLocked = entry?.mode === "Cash" && entry?.status === "Reimbursed";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.entity_code) return toast.error("Entity is required");
    if (!form.category_id) return toast.error("Category is required");
    if (isImprest && !form.payee_employee_id) return toast.error("Employee is required");
    if (!isImprest && !form.site_id && !form.equipment_id) return toast.error("Select a Site or Equipment");
    if (!form.payment_date) return toast.error("Payment Date is required");
    if (form.amount === "" || Number(form.amount) <= 0) return toast.error("Amount must be greater than 0");
    if (!isImprest && form.vendor_bill_id && (form.bill_amount_applied === "" || Number(form.bill_amount_applied) <= 0)) {
      return toast.error("Amount Applied to the Bill must be greater than 0");
    }
    if (!isImprest && form.payee_type === "Vendor" && !form.vendor_code) return toast.error("Select a Vendor to pay to the Vendor's bank account — or change Paid To");
    if (!isImprest && form.payee_type === "Employee" && !form.payee_employee_id) return toast.error("Select the Employee to pay");
    setSaving(true);
    const payload = {
      entity_code: form.entity_code,
      site_id: isImprest ? null : form.site_id || null,
      equipment_id: isImprest ? null : form.equipment_id || null,
      category_id: form.category_id,
      vendor_code: isImprest ? null : form.vendor_code || null,
      vendor_bill_id: isImprest ? null : form.vendor_bill_id || null,
      bill_amount_applied: !isImprest && form.vendor_bill_id ? Number(form.bill_amount_applied) : null,
      amount: Number(form.amount),
      payment_date: form.payment_date,
      reference: form.reference.trim() || null,
      purpose: form.purpose.trim() || null,
      payee_type: isImprest ? "Employee" : form.payee_type,
      payee_employee_id: isImprest || form.payee_type === "Employee" ? form.payee_employee_id : null,
      payee_note: !isImprest && form.payee_type === "Other" ? form.payee_note.trim() || null : null,
    };
    try {
      if (correctionMode) {
        await client.put(`/payment-desk/entries/${entry.entry_id}/correct`, { ...payload, mode: form.mode });
        try {
          if (newFile) {
            const fd = new FormData();
            fd.append("file", newFile);
            await client.post(`/payment-desk/entries/${entry.entry_id}/attachment`, fd);
          } else if (removeAttachment && entry.attachment) {
            await client.delete(`/payment-desk/entries/${entry.entry_id}/attachment`);
          }
        } catch (err) {
          toast.error(`Fields saved, but the attachment change failed: ${getErrorMessage(err)}`);
          onSaved();
          return;
        }
        toast.success("Entry updated");
      } else {
        const res = await client.post(base, payload);
        toast.success(`${mode} Entry logged — ${res.data.entry_id}`);
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`${mode.toLowerCase()}-form-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? `${entry.mode || mode} Entry — ${entry.entry_id}` : `Log ${mode} Entry`}
            {isEdit && <Badge className={entry.status === "Reimbursed" ? "bg-emerald-600" : "bg-amber-500"}>{entry.status}</Badge>}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {readOnly && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="postfacto-form-locked-note">
              Edit window closed — this entry can only be edited within 15 days of its created date (Director: any time).
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Entity *</label>
              <SearchableSelect testId="postfacto-form-entity-select" loading={refData.loadingLists.entities} value={form.entity_code} onValueChange={(v) => set("entity_code", v)} disabled={readOnly} placeholder="Select entity"
  options={refData.entities.map((en) => ({ value: en.code, label: en.name, testId: `postfacto-form-entity-option-${en.code}` }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Category *</label>
              <SearchableSelect testId="postfacto-form-category-select" loading={refData.loadingLists.categories} value={form.category_id} onValueChange={(v) => setForm((f) => reshapeForCategory(f, refData.categories, v))} disabled={readOnly} placeholder="Select category"
  options={refData.categories.map((c) => ({ value: c.id, label: c.name, testId: `postfacto-form-category-option-${c.id}` }))} />
            </div>
          </div>

          {!isImprest && <fieldset disabled={readOnly} className="contents">
            <AttributionField
              siteId={form.site_id}
              equipmentId={form.equipment_id}
              sites={refData.sites}
              equipment={refData.equipment} sitesLoading={refData.loadingLists.sites} equipmentLoading={refData.loadingLists.equipment}
              onChange={({ siteId, equipmentId }) => setForm((f) => ({ ...f, site_id: siteId, equipment_id: equipmentId }))}
              testId="postfacto-form-attribution"
            />
          </fieldset>}

          <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
            {isImprest && (
              <div className="col-span-2" data-testid="postfacto-form-imprest-employee-section">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employee *</label>
                <SearchableSelect testId="postfacto-form-imprest-employee-select" loading={refData.loadingLists.payeeEmployees} value={form.payee_employee_id} onValueChange={(v) => set("payee_employee_id", v)} disabled={readOnly} placeholder="Select employee"
  options={(refData.payeeEmployees || []).map((e) => ({ value: e.employee_id, label: `${e.employee_id} — ${e.name}`, testId: `postfacto-form-imprest-employee-option-${e.employee_id}` }))} />
                <p className="text-xs text-slate-500 mt-1">Imprest advance — counted in this employee's Imprest balance.</p>
              </div>
            )}
            {!isImprest && <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Vendor {form.vendor_bill_id ? "*" : ""}</label>
              <SearchableSelect testId="postfacto-form-vendor-select" loading={refData.loadingLists.vendors} value={form.vendor_code || "none"} placeholder="None" disabled={readOnly}
  onValueChange={(v) => v === "__add_vendor__" ? setShowAddVendor(true) : changeVendor(v === "none" ? "" : v)}
  options={[
    { value: "none", label: "None", pinned: true },
    ...refData.vendors.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code, testId: `postfacto-form-vendor-option-${v.vendor_code}` })),
    ...(canManageVendor ? [{ value: "__add_vendor__", label: "+ Add New Vendor", pinned: true, className: "text-amber-600 font-medium", testId: "postfacto-form-add-new-vendor" }] : []),
  ]} />
            </div>}
            {!isImprest && <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Linked Vendor Bill (optional)</label>
              <SearchableSelect testId="postfacto-form-bill-select" value={form.vendor_bill_id || "none"} onValueChange={(v) => set("vendor_bill_id", v === "none" ? "" : v)} disabled={readOnly || !form.vendor_code}
  placeholder={form.vendor_code ? "None" : "Select a Vendor first"} options={[
    { value: "none", label: "None", pinned: true },
    ...outstandingBills.map((b) => ({ value: b.bill_id, label: `${b.bill_id} — ${money(b.outstanding_balance)} outstanding`, testId: `postfacto-form-bill-option-${b.bill_id}` })),
  ]} />
              {selectedBill && (
                <p className="text-xs text-slate-500 mt-1" data-testid="postfacto-form-bill-balance">
                  Bill {selectedBill.bill_id}: {money(selectedBill.amount)} billed · {money(selectedBill.outstanding_balance)} outstanding
                </p>
              )}
            </div>}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount (₹) *</label>
              <Input type="number" step="any" data-testid="postfacto-form-amount-input" value={form.amount} onChange={(e) => set("amount", e.target.value)} disabled={readOnly} />
            </div>
            {!isImprest && form.vendor_bill_id && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Amount Applied to Bill (₹) *</label>
                <Input type="number" step="any" data-testid="postfacto-form-applied-input" value={form.bill_amount_applied} onChange={(e) => set("bill_amount_applied", e.target.value)} disabled={readOnly} />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{mode} Payment Date *</label>
              <Input type="date" data-testid="postfacto-form-date-input" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} disabled={readOnly} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">{mode === "Cash" ? "Voucher No. (optional)" : "UPI Transaction Ref (optional)"}</label>
              <Input data-testid="postfacto-form-reference-input" value={form.reference} onChange={(e) => set("reference", e.target.value)} disabled={readOnly} />
            </div>
          </div>

          {!isImprest && <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4" data-testid="postfacto-form-paid-to-section">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Paid To *</label>
              <Select
                value={form.payee_type}
                disabled={readOnly}
                onValueChange={(v) => {
                  setPayeeTouched(true);
                  set("payee_type", v);
                }}
              >
                <SelectTrigger data-testid="postfacto-form-paid-to-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vendor" data-testid="postfacto-form-paid-to-option-vendor">Vendor's bank account</SelectItem>
                  <SelectItem value="Employee" data-testid="postfacto-form-paid-to-option-employee">Employee's bank account</SelectItem>
                  <SelectItem value="Other" data-testid="postfacto-form-paid-to-option-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.payee_type === "Employee" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Employee *</label>
                <SearchableSelect testId="postfacto-form-payee-employee-select" loading={refData.loadingLists.payeeEmployees} value={form.payee_employee_id} onValueChange={(v) => set("payee_employee_id", v)} disabled={readOnly} placeholder="Select employee"
  options={(refData.payeeEmployees || []).map((e) => ({ value: e.employee_id, label: `${e.employee_id} — ${e.name}`, testId: `postfacto-form-payee-employee-option-${e.employee_id}` }))} />
              </div>
            )}
            {form.payee_type === "Other" && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Payee Note (optional)</label>
                <Input data-testid="postfacto-form-payee-note-input" placeholder="e.g. name of the person paid" value={form.payee_note} onChange={(e) => set("payee_note", e.target.value)} disabled={readOnly} />
              </div>
            )}
          </div>}

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Purpose / Remarks</label>
            <Textarea data-testid="postfacto-form-purpose-input" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} disabled={readOnly} />
          </div>

          {correctionMode && (
            <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4" data-testid="postfacto-form-correction-section">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Payment Mode</label>
                <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                  <SelectTrigger data-testid="postfacto-form-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash" data-testid="postfacto-form-mode-option-cash">Cash</SelectItem>
                    <SelectItem value="UPI" disabled={switchLocked} data-testid="postfacto-form-mode-option-upi">UPI</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">RTGS/NEFT cannot be switched to or from — enter a new entry instead.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Attachment</label>
                {entry.attachment && !removeAttachment && !newFile && (
                  <p className="text-xs text-slate-600 mb-1.5" data-testid="postfacto-form-attachment-current">
                    Current: {entry.attachment.filename}{" "}
                    <button type="button" className="text-red-600 underline ml-2" data-testid="postfacto-form-attachment-remove" onClick={() => setRemoveAttachment(true)}>Remove</button>
                  </p>
                )}
                {removeAttachment && <p className="text-xs text-amber-600 mb-1.5" data-testid="postfacto-form-attachment-removed">Attachment will be removed on save.</p>}
                <Input type="file" accept="image/*,application/pdf" data-testid="postfacto-form-attachment-input" onChange={(e) => setNewFile(e.target.files?.[0] || null)} />
              </div>
            </div>
          )}

          {isEdit && <ChangeLog entity={entry} testId="postfacto-form-change-log" />}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="postfacto-form-cancel-button">
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button type="submit" disabled={saving} data-testid="postfacto-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
                {saving ? "Saving..." : isEdit ? "Save Changes" : `Log ${mode} Entry`}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>

      {showAddVendor && (
        <VendorFormDialog
          vendor={null}
          onClose={() => setShowAddVendor(false)}
          onSaved={(newVendor) => {
            setShowAddVendor(false);
            setForm((f) => ({ ...f, vendor_code: newVendor.vendor_code, vendor_bill_id: "" }));
          }}
        />
      )}
    </Dialog>
  );
}
